/* eslint-disable no-unused-vars */

import { some } from 'lodash'

/**
 * This middleware bootstraps the state, performs some validations like penalties, and defines
 * the current step for the championship registration flow.
 *
 * Another motivation for creating this middleware was to avoid repeated requests to the API, caused
 * by the `pages/athletes` page. The `<nuxt-child />` of this page, when combined with other
 * components, e.g., `<base-layout-greeting />`, executes the `fetch` hook for each component,
 * generating duplicate API requests. This middleware itself is also called multiple time, but the
 * `championshipRegistrationForm/bootstrap` prevents the duplicated requests.
 *
 *
 * TODO: Aparentemente temos um — pequeno — problema de UX ao utilizar um middleware. Ao clicar em um
 * link, a nova página será exibida APENAS quando todo o middleware for processado, ou seja,
 * um middleware lento prende o user na página atual, impedindo de apresentarmos skeletons
 * já na nova página. Isso é perceptível na página entre a página do combo e a edição da inscrição.
 */
export default async function({ route, redirect, app, store, error }) {
  const { path, query } = route

  if (!query.championshipId) {
    console.debug('championshipId is not defined. Redirecting to the championship list page...')
    return redirect(app.localePath({ name: 'athletes-championships' }))
  }

  store.dispatch('loading/setLoading', true, { root: true })

  /**
   * Ensure the championship invitations are loaded
   */
  await store.dispatch('championshipInvitation/load', app.$auth.user.athlete)

  /**
   * Find current step based on Nuxt page's `meta` option `currentStep`.
   */
  const currentStep = route.meta.find(meta => Object.hasOwn(meta, 'currentStep'))?.currentStep

  if (!currentStep) {
    throw new Error('Championship Registration step page must define a meta "currentStep"')
  }

  /**
   * Bootstrap state (fetch all required data and build registrations objects).
   */
  await store.dispatch('championshipRegistrationForm/bootstrap', {
    currentStep,
    championshipId: query.championshipId,
    registrationId: query.registrationId,
    isCombo: query.isCombo?.toString() === 'true'
  })

  /**
   * Run requirements validation only when the page opts-in via route meta.
   * This avoids tight coupling to route names and prevents bugs on file moves.
   *
   * TODO: When merge with `athlete-championship-registrations-part-2` we need to include
   * `requirementsValidation` in meta of `registrations/:id` (old `resume` page).
   */
  const shouldValidateRequirements = some(route.meta, meta => meta.requirementsValidation === true)

  if (shouldValidateRequirements) {
    const registrations = store.state.championshipRegistrationForm.registrations || []

    for (const registration of registrations) {
      const requirements = store.getters[
        'championshipRegistrationForm/requirementsForRegistration'
      ](registration)

      if (requirements.length > 0) {
        const [firstRequirement] = requirements

        /* prettier-ignore */
        const messagesMap = {
          hasActivePenalty:                     app.i18n.t('domain.championshipRegistration.requirement.hasActivePenalty'),
          membershipInactive:                   app.i18n.t('domain.championshipRegistration.requirement.membershipInactive'),
          membershipInactiveOnChampionshipDate: app.i18n.t('domain.championshipRegistration.requirement.membershipInactiveOnChampionshipDate'),
          registrationNotStarted:               app.i18n.t('domain.championshipRegistration.requirement.registrationNotStarted'),
          registrationClosed:                   app.i18n.t('domain.championshipRegistration.requirement.registrationClosed'),
          capacityClosed:                       app.i18n.t('domain.championshipRegistration.requirement.capacityClosed'),
          noDivisionsAvailable:                 app.i18n.t('domain.championshipRegistration.requirement.noDivisionsAvailable'),
          noWeightAvailable:                    app.i18n.t('domain.championshipRegistration.requirement.noWeightAvailable')
        }

        error({
          statusCode: 403,
          message: messagesMap[firstRequirement] || app.i18n.t('genericErrors.internalServer')
        })
      }
    }
  }

  store.dispatch('loading/setLoading', false, { root: true })
}
